import { useState, FormEvent } from 'react';
import PageHeader from '../components/PageHeader';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { isIOS } from '../services/platform.service';

export default function HelpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/contact`,
        { name, email, subject, message },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la trimitere mesaj. Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Contact & Ajutor" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Contact Info Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Avem nevoie de ajutor? Contactează-ne!
            </h2>
            <p className="text-gray-600 text-lg">
              Suntem aici să te ajutăm cu orice întrebări sau probleme ai avea.
            </p>
          </div>

          {/* Contact Cards */}
          <div className="mb-4 text-center">
            <p className="text-sm text-gray-600">
              <strong>SC GONGA SHOP SRL</strong> · CUI: RO40071310
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-full mb-4">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
              <a href="mailto:support@fiifit.ro" className="text-blue-600 hover:underline">
                support@fiifit.ro
              </a>
            </div>

            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-600 rounded-full mb-4">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Telefon</h3>
              <a href="tel:+40787569002" className="text-green-600 hover:underline">
                0787 569 002
              </a>
            </div>

            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-600 rounded-full mb-4">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Adresă</h3>
              <p className="text-gray-700">Strada Nicolae Titulescu 16,<br />Agigea, Constanța</p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-6">
            <MessageSquare className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold">Trimite-ne un mesaj</h2>
          </div>

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-green-600 text-sm font-medium">
                Mesajul tău a fost trimis cu succes! Îți vom răspunde în curând.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume Complet *
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ion Popescu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@exemplu.ro"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subiect *
              </label>
              <input
                type="text"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Despre ce vrei să discutăm?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mesaj *
              </label>
              <textarea
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Descrie problema sau întrebarea ta..."
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Se trimite...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Trimite Mesaj
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mt-6">
          <h2 className="text-xl font-semibold mb-6">Întrebări Frecvente</h2>

          {/* Category: Contul Meu și Utilizare */}
          <div className="mb-8">
            <h3 className="text-base font-bold text-blue-700 uppercase tracking-wide mb-4">
              Contul Meu și Utilizare
            </h3>
            <div className="space-y-4">
              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-2">Pe ce dispozitive pot folosi aplicația FII FIT?</h4>
                <p className="text-gray-600 text-sm">
                  FII FIT este disponibilă pe toate platformele majore. O poți descărca pe telefon {isIOS() ? 'din App Store' : 'din App Store (iOS) sau Google Play (Android)'}, ori o poți accesa direct din browserul calculatorului tău pe www.fiifit.ro. Contul tău se sincronizează automat în timp real pe toate dispozitivele.
                </p>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-2">Cum îmi pot șterge contul definitiv?</h4>
                <p className="text-gray-600 text-sm">
                  Dacă decizi să renunți la serviciile noastre, poți face acest lucru din setările contului tău. Mergi la secțiunea „Șterge contul", introdu cuvântul „DELETE" pentru a confirma acțiunea, iar contul și toate datele tale asociate vor fi șterse definitiv și ireversibil din sistemul nostru.
                </p>
              </div>

              <div className="pb-4">
                <h4 className="font-medium text-gray-900 mb-2">Cum pot contacta echipa de suport dacă am o problemă?</h4>
                <p className="text-gray-600 text-sm">
                  Suntem aici să te ajutăm! Ne poți trimite un mesaj oricând la adresa de email <a href="mailto:suport@fiifit.ro" className="text-blue-600 hover:underline">suport@fiifit.ro</a>. Echipa noastră îți va răspunde în cel mai scurt timp pentru a rezolva orice dificultate tehnică.
                </p>
              </div>
            </div>
          </div>

          {/* Category: Sistemul de Credite AI */}
          <div className="mb-8">
            <h3 className="text-base font-bold text-blue-700 uppercase tracking-wide mb-4">
              Sistemul de Credite AI
            </h3>
            <div className="space-y-4">
              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-2">La ce folosesc creditele în aplicația FII FIT?</h4>
                <p className="text-gray-600 text-sm mb-2">
                  Creditele reprezintă „moneda" prin care deblochezi funcționalitățile noastre avansate bazate pe Inteligență Artificială. Folosești credite pentru:
                </p>
                <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 pl-2">
                  <li>Conversațiile cu asistentul AI (Chat Nutriționist și Chat Terapeutic).</li>
                  <li>Scanarea imaginilor cu mâncare pentru a identifica automat alimentele și caloriile.</li>
                  <li>Generarea planurilor alimentare personalizate.</li>
                  <li>Generarea listelor inteligente de alimente permise sau interzise.</li>
                </ul>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-2">Cum obțin credite?</h4>
                <p className="text-gray-600 text-sm">
                  În funcție de planul tău (Gratuit, Basic, Premium sau Gold), contul tău este alimentat automat la începutul fiecărei luni cu un număr fix de credite. Dacă ai nevoie de mai multă asistență AI și îți epuizezi creditele înainte de finalul lunii, poți apăsa butonul „Cumpără Credite Extra" pentru a achiziționa pachete suplimentare sau credite individuale.
                </p>
              </div>

              <div className="pb-4">
                <h4 className="font-medium text-gray-900 mb-2">Creditele nefolosite se transferă pentru luna următoare?</h4>
                <p className="text-gray-600 text-sm">
                  Nu. Creditele incluse în abonamentul tău lunar se resetează la începutul fiecărui ciclu de facturare (de exemplu, pe data de 1 a fiecărei luni). Acestea nu se raportează pentru luna următoare, așa că te încurajăm să le folosești la maximum pentru sănătatea ta!
                </p>
              </div>
            </div>
          </div>

          {/* Category: Abonamente și Plăți */}
          <div>
            <h3 className="text-base font-bold text-blue-700 uppercase tracking-wide mb-4">
              Abonamente și Plăți
            </h3>
            <div className="space-y-4">
              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-2">Ce planuri de abonament oferă FII FIT?</h4>
                <p className="text-gray-600 text-sm mb-2">Oferim soluții pentru fiecare nevoie:</p>
                <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 pl-2">
                  <li><span className="font-medium">Planul Gratuit:</span> Îți oferă acces la funcțiile de bază (tracking complet, forum, articole) și 10 credite AI bonus lunar.</li>
                  <li><span className="font-medium">Planul Basic (€5.99/lună):</span> Deblochează Chat-ul AI, planurile alimentare (7 zile) și scanarea alimentelor, plus o alocare lunară generoasă de credite.</li>
                  <li><span className="font-medium">Planul Premium (€9.99/lună):</span> Cel mai popular plan. Include analitică avansată, mai multe planuri alimentare, export de date (PDF/CSV) și suport prioritar.</li>
                  <li><span className="font-medium">Planul Gold (€19.99/lună):</span> Food scan AI, chat AI, mai multe planuri alimentare, export de date (PDF/CSV), planurile alimentare (7 zile), consultanță personalizată 1-la-1 și suport VIP 24/7.</li>
                </ul>
              </div>

              <div className="border-b pb-4">
                <h4 className="font-medium text-gray-900 mb-2">Cum plătesc și care este perioada de facturare?</h4>
                <p className="text-gray-600 text-sm">
                  Facturarea se face lunar. Poți achita comod și sigur direct din aplicația de pe telefon (prin Apple Pay sau Google Pay) sau direct pe platforma web.
                </p>
              </div>

              <div className="pb-4">
                <h4 className="font-medium text-gray-900 mb-2">Pot schimba sau anula abonamentul oricând?</h4>
                <p className="text-gray-600 text-sm">
                  Da, ai control total. Poți face upgrade (trecere la un plan superior) sau downgrade oricând dorești, din secțiunea „Gestionare Facturare". Dacă dorești să anulezi abonamentul, o poți face direct din aplicație sau din setările abonamentelor de pe telefonul tău, iar beneficiile Premium vor rămâne active până la finalul lunii deja plătite.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
